import { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { GetAthleteInvitePlainText } from 'functions/textUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function AthleteInviteView() {

    const defaultAthleteEmailObject = {
        email: '',
        validationError: ''
    }

    const navigate = useNavigate()

    const { ShowModal } = useContext(ModalContext)

    const userId = useSelector((state) => state.user.id)
    const organizationId = useSelector((state) => state.user.organizationId)
    const organizationName = useSelector((state) => state.user.organizationName)
    const organizationCode = useSelector((state) => state.user.organizationCode)

    const [athleteEmails, setAthleteEmails] = useState([JSON.parse(JSON.stringify(defaultAthleteEmailObject))])
    const [invitesLoading, setInvitesLoading] = useState(false)

    const ShowAthleteInviteImportModal = () => {
        const modalData = {
            key: ModalKey.FileImport,
            size: 'third',
            titleText: 'Upload a CSV file of athlete emails',
            fileFormatDescriptionText: 'The CSV file should contain a list of emails each separated by a newline',
            exampleFileObject: (
                <div className='w-full bg-gray-100 pt-5 px-4 pb-4 rounded-md relative'>
                    <p>athlete1@gmail.com</p>
                    <p>athlete2@gmail.com</p>
                    <p>athlete3@gmail.com</p>

                    <div className='absolute top-0 left-0 text-xs text-gray-400 font-semibold py-1 px-2'>example_athletes.csv</div>
                </div>
            ),
            buttonText: 'Import Athletes',
            buttonAction: (fileData) => ProcessCSVFile(fileData)
        }

        ShowModal(modalData)
    }

    const ProcessCSVFile = (fileData) => {

        const reader = new FileReader()
        
        reader.onload = (e) => {

            const text = e.target.result
            const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n') // Normalize line endings (handle Windows \r\n, Mac \r, and Unix \n)
            
            // console.log(normalizedText)

            // Split by newline and filter out empty lines
            const csvFileDataArray = normalizedText.split('\n').map(line => line.trim()).filter(line => line.length > 0)
            
            // console.log(csvFileDataArray)
            
            setAthleteEmails(csvFileDataArray.map(email => ({
                email: email.trim().toLowerCase(),
                validationError: ''
            })))
        }
        
        reader.readAsText(fileData)

    }

    const HandleAthleteEmailChange = (index, email) => {
        var targetEmailObject = athleteEmails[index]

        targetEmailObject.email = email.trim().toLowerCase()
        targetEmailObject.validationError = ''

        setAthleteEmails(athleteEmails.map((e, i) => 
            i === index 
                ? targetEmailObject
                : e
        ))
    }

    const HandleRemoveAthleteEmail = (index) => {
        if (athleteEmails.length <= 1) return
        setAthleteEmails(athleteEmails.filter((_, i) => i !== index))
    }

    const ClearAllEmailErrors = () => {
        setAthleteEmails(athleteEmails.map(emailObj => ({
            ...emailObj,
            validationError: ''
        })))
    }

    const ValidateEmails = () => {

        var allEmailsValid = true
        
        var athleteEmailsWithValidationErrors = athleteEmails.map(emailObj => {
            const trimmedEmail = emailObj.email.trim()
            
            if (
                trimmedEmail === ''
                || !IsValidEmailRegex(trimmedEmail)
            ) {
                allEmailsValid = false

                return {
                    ...emailObj,
                    validationError: 'Invalid email address'
                }
            }
            
            return emailObj
        })
        
        setAthleteEmails(athleteEmailsWithValidationErrors)

        return allEmailsValid
    }

    const IsValidEmailRegex = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const OnSendInvites = async () => {

        ClearAllEmailErrors()

        if (athleteEmails.length === 0) {
            return
        }

        if (ValidateEmails() === false) {
            return
        }

        setInvitesLoading(true)

        try {
            const jsonBody = {
                coachId: userId,
                organizationId: organizationId,
                organizationName: organizationName,
                organizationCode: organizationCode,
                recipients: athleteEmails.map(emailObj => emailObj.email)
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('EMAIL', 'POST', '/invite_athletes_to_organization', jsonBody)

            // console.log(result)

            if (result.data.status === "failure") {
                ShowFailureModal()
                setInvitesLoading(false)
                console.error(result)
                return
            }

            ShowSuccessModal()
            setInvitesLoading(false)

        } catch (error) {
            ShowFailureModal()
            setInvitesLoading(false)
            console.error(error)
        }
    }

    const ShowSuccessModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <CheckIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Success!',
            bodyText: [
                'Athlete invites have successfully been sent'
            ],
            buttonText: 'Return to Home',
            buttonAction: () => { navigate('/home') }
        }

        ShowModal(modalData)
    }

    const ShowFailureModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <ExclamationTriangleIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Error!',
            bodyText: [
                'Failed to send athlete invites. Please try again.',
                'If the problem persists, please contact support@kinesisintegrated.com'
            ],
            buttonText: 'Close'
        }

        ShowModal(modalData)
    }

    const ShowPlainTextEmailPreviewModal = () => {
        const modalData = {
            key: ModalKey.PlainTextEmailPreview,
            size: 'half',
            titleText: 'Athlete Invite Email Preview',
            bodyText: [
                'This is a preview of the plain text email that will be sent to the athletes.',
                'Click anywhere below to copy the text to your clipboard.'
            ],
            plainText: GetAthleteInvitePlainText(organizationName, organizationCode),
            includeSendFunctionality: false
        }

        ShowModal(modalData)
    }

    return (
        <ScreenFrame>
            <div className='max-w-4xl mx-auto space-y-4'>
                <div className='space-y-1 pb-2'>
                    <div className='flex items-center gap-x-2'>
                        <div className='space-y-1 flex-grow'>
                            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2">
                                Enter Athlete Emails Below
                            </h1>
                        </div>

                        <div className='w-60'>
                            <SolidButton
                                text="Import Athletes"
                                action={ShowAthleteInviteImportModal}
                                className='shadow-md'
                            />
                        </div>
                    </div>
                </div>

                {athleteEmails.map((emailObj, index) => (
                    <div 
                        key={index}
                        className='relative'
                    >
                        <div className='flex items-center gap-x-4'>
                            <input
                                type="text"
                                className={`
                                    ${emailObj.validationError.length > 0 ? 'bg-red-50' : 'bg-white'}
                                    flex-1 py-2 px-4 rounded-md border border-gray-200 placeholder-gray-500 shadow-md
                                    focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                `}
                                placeholder='Enter Athlete Email Here'
                                value={emailObj.email}
                                onChange={(event) => HandleAthleteEmailChange(index, event.target.value)}
                            />

                            <XMarkIcon 
                                className='h-5 w-5 text-gray-400 cursor-pointer' 
                                onClick={() => HandleRemoveAthleteEmail(index)} 
                            />
                        </div>

                        {emailObj.validationError && (
                            <div className='absolute bottom-3 right-12 pl-2 bg-red-50 text-red-600 text-sm z-10'>
                                {emailObj.validationError}
                            </div>
                        )}
                    </div>
                ))}

                <div>
                    <p className='-mt-1 text-sm text-gray-500'>{athleteEmails.length} athlete{athleteEmails.length === 1 ? '' : 's'} added</p>
                </div>
                
                <div className='pt-2 grid grid-cols-2 gap-x-4'>
                    <SolidButton
                        text="Add Athlete Email"
                        action={() => setAthleteEmails([...athleteEmails, { ...defaultAthleteEmailObject }])}
                        className='shadow-md'
                    />

                    <BrandColorButton 
                        text="Send Invites"
                        action={OnSendInvites}
                        isLoading={invitesLoading}
                        className='shadow-md'
                    />
                </div>

                <p 
                    className='pt-4 text-center text-sm text-blue-500 underline cursor-pointer'
                    onClick={ShowPlainTextEmailPreviewModal}
                >
                    What if I want to send the email myself?
                </p>
            </div>
        </ScreenFrame>
    )
}
